import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import styled from 'particles/themes/styled'
import SideNavigator from 'particles/SideNavigator'
import { AccountCreationForm, BusinessDetailForm, BusinessDetailValues, TeamSetupForm } from './Forms'
import { useRedirect, useWindowSize } from 'hooks'
import { RegisterActions } from 'services/api/register'
import { selectProfileDetails } from 'state'
import { selectUserIsRegistered, getProfileDetail } from 'state/profile'
import { AlertContentType } from 'particles/Alert'
import ReminderAlert from 'particles/Alert/ReminderAlert'
import RectangleButton from 'particles/Button/RectangleButton'
import Text from 'particles/Text'

const RegisterSetupContainer = styled.div`
    width: 100%;
    height: calc(100% - 65px);
    overflow: hidden;
    display: grid;
    grid-template-columns: auto auto 1fr;
    gap: ${({ theme }) => theme.spacing[400]};
    @media screen and (max-width: 767px) {
        display: flex;
        flex-direction: column;
        flex-direction: column-reverse;
        justify-content: space-between;
    }
    .divider {
        height: 100%;
        width: 1px;
        background-color: ${({ theme }) => theme.colors.divider.primary};
        @media screen and (max-width: 767px) {
            display: none;
        }
    }

    .form-wrapper {
        width: 100%;
        overflow-y: auto;
    }
    .form {
        width: 600px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin: 0 auto;
        gap: 24px;
        @media screen and (max-width: 767px) {
            width: 100%;
        }
    }
    .next-button {
        width: 100%;
    }
`
type Step = {
    title: string
    subtext: string
    isCommented: boolean
    isCompleted: boolean
    isDisabled: boolean
}

const stepOptions = (t: TFunction): Step[] => [
    {
        title: t('logIn.signUp.accountCreationTitle'),
        subtext: t('logIn.signUp.accountCreationSubTitle'),
        isCommented: false,
        isCompleted: false,
        isDisabled: false,
    },
    {
        title: t('logIn.signUp.businessDetailsTitle'),
        subtext: t('logIn.signUp.businessDetailsSubTitle'),
        isCommented: false,
        isCompleted: false,
        isDisabled: true,
    },
    {
        title: t('logIn.signUp.teamSetupTitle'),
        subtext: t('logIn.signUp.teamSetupSubTitle'),
        isCommented: false,
        isCompleted: false,
        isDisabled: true,
    },
]

interface SignUpPayloads extends BusinessDetailValues {
    username: string
}

type AlertConfig = {
    show: boolean
    configType: AlertContentType
    alertTitle: string
    alertSubtext: string
}

const initAlertState: AlertConfig = {
    show: false,
    configType: 'positive',
    alertTitle: '',
    alertSubtext: '',
}
const RegisterSetup: React.FC = () => {
    const [t] = useTranslation()
    const { redirect, redirectToLogin } = useRedirect()
    const dispatch = useDispatch()
    const isRegistered = useSelector(selectUserIsRegistered)
    const profile = useSelector(selectProfileDetails)
    const [signUpPayload, setSignUpPayload] = useState<SignUpPayloads>({
        username: '',
        business_type: null,
        company_name: null,
        industry: null,
        industry_others: null,
        country: null,
        business_link: '',
        business_contact_number: '',
    })
    const [alertConfig, setAlertConfig] = useState<AlertConfig>(initAlertState)
    const [steps, setSteps] = useState(stepOptions(t))

    const [selected, setSelected] = useState<number>(0)
    const windowSize = useWindowSize()

    const setErrorAlert = () => {
        setAlertConfig({
            ...alertConfig,
            show: true,
            configType: 'error',
            alertTitle: t('errorMessage.general.somethingWentWrongTitle'),
            alertSubtext: t('errorMessage.signUp.refreshScreen'),
        })
    }

    const onSelectStep = (index: number) => {
        if (index !== selected) {
            setAlertConfig(initAlertState)
        }
        setSelected(index)
    }

    const handleStepChange = (
        currentStepIndex: number,
        nextStepIndex: number | null,
        isCurrentDisable?: boolean,
        isPreviousDisable?: boolean,
    ) => {
        const newSteps = [...steps]
        const updateStep = { ...newSteps[currentStepIndex] }
        updateStep.isCompleted = true
        if (isCurrentDisable) {
            updateStep.isDisabled = true
        }
        newSteps[currentStepIndex] = updateStep
        if (isPreviousDisable) {
            const updatePrevStep = { ...newSteps[currentStepIndex - 1] }
            updatePrevStep.isDisabled = true
            newSteps[currentStepIndex - 1] = updatePrevStep
        }
        if (nextStepIndex) {
            const nextStep = { ...steps[nextStepIndex] }
            nextStep.isDisabled = nextStepIndex > steps.length
            newSteps[nextStepIndex] = nextStep
            setSelected(nextStepIndex)
            setAlertConfig(initAlertState)
        }
        setSteps(newSteps)
    }
    const onNext = useCallback(() => {
        dispatch(
            getProfileDetail({
                onSuccess: () => {
                    redirect(`/campaigns`)
                },
            }),
        )
    }, [])

    const storePayload = useCallback((step: number, data: SignUpPayloads) => {
        sessionStorage.setItem(`signup-${profile.uuid}`, JSON.stringify({ data, step }))
    }, [])

    const formTitle = useMemo(() => {
        switch (selected) {
            case 0:
                return t('logIn.signUp.aboutYouTitle')
            case 1:
                return t('logIn.signUp.aboutBusinessTitle')
            case 2:
                return t('logIn.signUp.addMemberTitle')
            case 3:
                return t('logIn.signUp.addMemberTitle')
            default:
                return null
        }
    }, [selected, t])

    useEffect(() => {
        const options = stepOptions(t)
        setSteps(prevSteps =>
            [...prevSteps].map((step: Step, index: number) => ({
                ...step,
                title: options[index].title,
                subtext: options[index].subtext,
            })),
        )
    }, [t])

    useEffect(() => {
        if (!profile.uuid || isRegistered) {
            redirectToLogin()
        } else {
            const previousData = sessionStorage.getItem(`signup-${profile.uuid}`)
            if (previousData) {
                const data = JSON.parse(previousData)
                const step = data?.step
                setSignUpPayload(data?.data)
                if (step === 2) {
                    handleStepChange(1, 2, true, true)
                } else {
                    handleStepChange(step - 1, step)
                }
                setSelected(step)
            }
        }
    }, [profile.uuid, isRegistered])

    return (
        <RegisterSetupContainer>
            <SideNavigator
                stepOptions={steps}
                selected={selected}
                onSelectStep={onSelectStep}
                isEditable={false}
                isHorizontal={windowSize <= 768}
                isExpanded={windowSize > 768}
            />
            <div className="divider" />
            <div className="form-wrapper">
                <div className="form">
                    {formTitle && <Text scale="H2">{formTitle}</Text>}
                    {alertConfig.show && (
                        <ReminderAlert
                            form={alertConfig?.configType}
                            title={alertConfig?.alertTitle}
                            subtext={alertConfig?.alertSubtext}
                            isClosable={false}
                            width="600px"
                        />
                    )}
                    {selected === 0 && (
                        <AccountCreationForm
                            onSubmit={async (value: string) => {
                                handleStepChange(0, 1)
                                setSignUpPayload({ ...signUpPayload, username: value })
                                storePayload(0, { ...signUpPayload, username: value })
                            }}
                            value={signUpPayload.username}
                        />
                    )}
                    {selected === 1 && (
                        <BusinessDetailForm
                            value={signUpPayload}
                            onSubmit={(value: BusinessDetailValues) => {
                                storePayload(1, { ...signUpPayload, ...value })
                                dispatch({
                                    type: RegisterActions.SIGN_UP_DETAILS,
                                    payload: {
                                        data: { ...signUpPayload, ...value },
                                        onSuccess: () => {
                                            setSignUpPayload({ ...signUpPayload, ...value })
                                            handleStepChange(1, 2, true, true)
                                            sessionStorage.removeItem(`signup-${profile.uuid}`)
                                        },
                                        onError: setErrorAlert,
                                    },
                                })
                            }}
                        />
                    )}
                    {selected === 2 && (
                        <TeamSetupForm
                            onNext={onNext}
                            onSuccessCallback={() => {
                                handleStepChange(2, null, true)
                                setSelected(3)
                                storePayload(2, { ...signUpPayload })
                                setAlertConfig({
                                    ...alertConfig,
                                    show: true,
                                    configType: 'positive',
                                    alertTitle: t('successMessage.signUp.emailSentTitle'),
                                    alertSubtext: t('successMessage.signUp.emailSentSubTitle'),
                                })
                            }}
                            onErrorCallback={setErrorAlert}
                        />
                    )}
                    {selected === 3 && (
                        <RectangleButton
                            type="button"
                            label={t('button.next')}
                            size="l"
                            onClick={onNext}
                            className="next-button"
                        />
                    )}
                </div>
            </div>
        </RegisterSetupContainer>
    )
}

export default RegisterSetup
